import { useState, useMemo } from 'react';
import type { Appointment, AvailabilitySlot, BlockedDate, AppointmentType } from '../../../mocks/appointments';
import { dayNames, statusLabels } from '../../../mocks/appointments';
import { calculateFranjaOccupancy } from '../../../utils/occupancy';

interface Props {
  appointments: Appointment[];
  slots: AvailabilitySlot[];
  blockedDates: BlockedDate[];
  types: AppointmentType[];
}

function formatDateISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const monthShort = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

export default function UpcomingOverview({ appointments, slots, blockedDates, types }: Props) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const today = new Date();
    const base = new Date(today);
    base.setDate(base.getDate() + weekOffset * 7);
    // Monday-start
    const dayOfWeek = base.getDay();
    const diff = base.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(base.setDate(diff));
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekOffset]);

  const goPrevWeek = () => setWeekOffset((o) => o - 1);
  const goNextWeek = () => setWeekOffset((o) => o + 1);
  const goToday = () => { setWeekOffset(0); setSelectedDay(null); };

  const dayData = useMemo(() => {
    return weekDays.map((date) => {
      const iso = formatDateISO(date);
      const dow = date.getDay();
      const isBlocked = blockedDates.some((b) => b.date === iso);
      const hasSlots = slots.some((s) => s.day_of_week === dow && s.is_active);
      const franjas = calculateFranjaOccupancy(iso, dow, slots, appointments, types);
      const dayAppts = appointments.filter(
        (a) => a.preferred_date === iso && a.status !== 'cancelled',
      );

      const totalFranjas = franjas.reduce((s, f) => s + f.maxBookings, 0);
      const totalBooked = franjas.reduce((s, f) => s + f.booked, 0);
      const occupancyPct = totalFranjas > 0 ? Math.round((totalBooked / totalFranjas) * 100) : 0;

      return {
        date,
        iso,
        dow,
        isBlocked,
        hasSlots,
        franjas,
        totalFranjas,
        totalBooked,
        occupancyPct,
        appointments: dayAppts,
      };
    });
  }, [weekDays, slots, appointments, blockedDates, types]);

  const totalUpcomingBooked = dayData.reduce((sum, d) => sum + d.totalBooked, 0);
  const totalUpcomingCapacity = dayData.reduce((sum, d) => sum + d.totalFranjas, 0);

  const isTodayWeek = weekOffset === 0;

  const selectedDayData = selectedDay ? dayData.find((d) => d.iso === selectedDay) : null;

  return (
    <div className="space-y-6">
      {/* Header + navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={goPrevWeek}
            className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <h2 className="font-serif text-brand-black text-2xl font-medium whitespace-nowrap">
            {isTodayWeek ? 'Próximos 7 días' : `Semana de ${weekDays[0].getDate()} ${monthShort[weekDays[0].getMonth()]}`}
          </h2>
          <button
            onClick={goNextWeek}
            className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
          {!isTodayWeek && (
            <button
              onClick={goToday}
              className="px-3 py-1.5 rounded-full bg-brand-gray-100 font-sans text-xs text-brand-gray-600 hover:text-brand-black transition-colors whitespace-nowrap"
            >
              Hoy
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-brand-gray-50 rounded-lg px-3 py-2">
            <i className="ri-calendar-check-line text-brand-gray-500 text-sm" />
            <span className="font-sans text-brand-gray-600 text-sm">
              {totalUpcomingBooked} / {totalUpcomingCapacity} citas
            </span>
          </div>
          <div className="flex items-center gap-1.5 bg-emerald-50 rounded-lg px-3 py-2">
            <i className="ri-door-open-line text-emerald-600 text-sm" />
            <span className="font-sans text-emerald-700 text-sm font-medium">
              {totalUpcomingCapacity - totalUpcomingBooked} libres
            </span>
          </div>
        </div>
      </div>

      {/* Week grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {dayData.map((day) => {
          const isToday = new Date().toDateString() === day.date.toDateString();
          const isUrgent = !day.isBlocked && day.hasSlots && day.occupancyPct >= 90;
          const isSelected = selectedDay === day.iso;

          let statusColor = 'bg-emerald-50 border-emerald-200';
          let statusText = 'text-emerald-700';
          let statusLabel = 'Libre';
          let statusIcon = 'ri-door-open-line';

          if (day.isBlocked) {
            statusColor = 'bg-red-50 border-red-200';
            statusText = 'text-red-700';
            statusLabel = 'Bloqueado';
            statusIcon = 'ri-lock-line';
          } else if (!day.hasSlots) {
            statusColor = 'bg-brand-gray-100 border-brand-gray-200';
            statusText = 'text-brand-gray-500';
            statusLabel = 'Sin franjas';
            statusIcon = 'ri-time-line';
          } else if (day.occupancyPct >= 90) {
            statusColor = 'bg-red-50 border-red-200';
            statusText = 'text-red-700';
            statusLabel = 'Lleno';
            statusIcon = 'ri-fire-line';
          } else if (day.occupancyPct >= 60) {
            statusColor = 'bg-amber-50 border-amber-200';
            statusText = 'text-amber-700';
            statusLabel = 'Ocupado';
            statusIcon = 'ri-alert-line';
          } else if (day.occupancyPct > 0) {
            statusLabel = 'Moderado';
            statusIcon = 'ri-user-line';
          }

          return (
            <button
              key={day.iso}
              onClick={() => setSelectedDay(isSelected ? null : day.iso)}
              className={`text-left border rounded-2xl p-4 flex flex-col transition-all hover:border-brand-gray-400 ${
                isSelected
                  ? 'border-brand-black bg-brand-gray-50'
                  : isToday
                  ? 'border-brand-black bg-white'
                  : 'border-brand-gray-200 bg-white'
              }`}
            >
              {isUrgent && (
                <div className="mb-2 bg-red-600 rounded-md px-2 py-1 flex items-center justify-center gap-1">
                  <i className="ri-alarm-warning-line text-white text-xs" />
                  <span className="font-sans text-white text-[10px] font-semibold uppercase tracking-wider">
                    URGENTE
                  </span>
                </div>
              )}

              <div className="text-center mb-3">
                <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">
                  {dayNames[day.dow].slice(0, 3)}
                </p>
                <p className="font-serif text-brand-black text-2xl font-medium mt-0.5">
                  {day.date.getDate()}
                </p>
                <p className="font-sans text-brand-gray-400 text-xs">
                  {monthShort[day.date.getMonth()]}
                </p>
              </div>

              <div className={`rounded-lg px-2 py-1.5 text-center mb-3 ${statusColor}`}>
                <div className="flex items-center justify-center gap-1">
                  <i className={`${statusIcon} ${statusText} text-xs`} />
                  <span className={`font-sans text-xs font-medium ${statusText}`}>
                    {statusLabel}
                  </span>
                </div>
              </div>

              {day.hasSlots && !day.isBlocked && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-sans text-brand-gray-500 text-[11px]">
                      Ocupación
                    </span>
                    <span className={`font-sans text-[11px] font-medium ${isUrgent ? 'text-red-600' : 'text-brand-black'}`}>
                      {day.occupancyPct}%
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-brand-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        day.occupancyPct >= 90
                          ? 'bg-red-400'
                          : day.occupancyPct >= 60
                            ? 'bg-amber-400'
                            : 'bg-emerald-400'
                      }`}
                      style={{ width: `${Math.min(day.occupancyPct, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="font-sans text-brand-gray-500 text-[10px]">
                      {day.totalBooked} citas
                    </span>
                    <span className="font-sans text-brand-gray-500 text-[10px]">
                      {day.totalFranjas - day.totalBooked} libres
                    </span>
                  </div>
                </div>
              )}

              {/* Franja previews */}
              {day.franjas.length > 0 && (
                <div className="space-y-1 mb-2">
                  {day.franjas.slice(0, 2).map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-2 py-1 rounded-md bg-brand-gray-50"
                    >
                      <span className="font-sans text-[10px] text-brand-gray-500">
                        {f.start}—{f.end}
                      </span>
                      <span
                        className={`font-sans text-[10px] font-medium ${
                          f.pct >= 90 ? 'text-red-600' : f.pct >= 60 ? 'text-amber-600' : 'text-emerald-600'
                        }`}
                      >
                        {f.booked}/{f.maxBookings}
                      </span>
                    </div>
                  ))}
                  {day.franjas.length > 2 && (
                    <p className="font-sans text-brand-gray-400 text-[10px] text-center">
                      +{day.franjas.length - 2} franjas
                    </p>
                  )}
                </div>
              )}

              {/* Appointment previews */}
              <div className="flex-1 space-y-1 min-h-[60px]">
                {day.appointments.length > 0 ? (
                  day.appointments.slice(0, 3).map((a) => {
                    const st = statusLabels[a.status];
                    return (
                      <div
                        key={a.id}
                        className={`rounded-md px-2 py-1 ${st?.bg}`}
                      >
                        <p className={`font-sans text-[10px] font-medium ${st?.color}`}>
                          {a.preferred_time} {a.name.split(' ')[0]}
                        </p>
                        <p className="font-sans text-[9px] text-brand-gray-500 truncate">
                          {a.service_type}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <p className="font-sans text-brand-gray-300 text-xs text-center py-2">
                    Sin citas
                  </p>
                )}
                {day.appointments.length > 3 && (
                  <p className="font-sans text-brand-gray-400 text-[10px] text-center">
                    +{day.appointments.length - 3} más
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected day detail panel */}
      {selectedDayData && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-brand-gray-200 bg-brand-gray-50/50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-500">
                <i className="ri-calendar-line" />
              </div>
              <div>
                <h3 className="font-serif text-brand-black text-lg font-medium">
                  {dayNames[selectedDayData.dow]} {selectedDayData.date.getDate()} {monthShort[selectedDayData.date.getMonth()]}
                </h3>
                <p className="font-sans text-brand-gray-500 text-xs">
                  {selectedDayData.totalBooked} / {selectedDayData.totalFranjas} citas · {selectedDayData.totalFranjas - selectedDayData.totalBooked} libres
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedDay(null)}
              className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <div className="divide-y divide-brand-gray-200">
            {selectedDayData.franjas.map((franja, idx) => {
              const isFull = franja.available === 0;
              const isHigh = franja.pct >= 60 && !isFull;

              return (
                <div key={idx} className="px-5 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-gray-100">
                        <i className="ri-time-line text-brand-gray-500" />
                      </div>
                      <div>
                        <p className="font-sans text-brand-black text-sm font-medium">
                          {franja.start} — {franja.end}
                        </p>
                        <p className="font-sans text-brand-gray-400 text-xs">
                          Capacidad: {franja.maxBookings} citas
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 font-sans text-xs font-medium px-2.5 py-1 rounded-full ${
                        isFull
                          ? 'bg-red-50 text-red-600'
                          : isHigh
                          ? 'bg-amber-50 text-amber-600'
                          : 'bg-emerald-50 text-emerald-600'
                      }`}
                    >
                      {franja.booked} / {franja.maxBookings}
                      {isFull && <i className="ri-close-circle-line" />}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-brand-gray-100 rounded-full overflow-hidden mb-3">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isFull ? 'bg-red-400' : isHigh ? 'bg-amber-400' : 'bg-emerald-400'
                      }`}
                      style={{ width: `${Math.min(100, franja.pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}